import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spin, Typography, Timeline, Select, Alert, Image } from "antd";
import moment from "moment";
import {
  getReportedById,
  getPmpCrfRecord,
  getVisitsByScaleCode,
} from "@/services/data/patientSelfReported";
import "./pmpScaleModal.less";

const { Text, Paragraph } = Typography;

interface FieldNode {
  type?: string;
  label?: string;
  fieldId?: string;
  nodeType?: string;
  required?: boolean;
  listType?: string;
  maxCount?: number;
  options?: Array<{ label: string; value: any; score?: number }>;
  children?: FieldNode[];
}

interface SchemaSnapshot {
  name?: string;
  description?: string;
  root?: FieldNode;
}

interface RecordItem {
  scalaServerRecordId?: string | number;
  createTime?: string;
  [key: string]: any;
}

interface PmpScaleModalProps {
  visible: boolean;
  scaleCode?: string;
  visitId?: string | number;
  subjectCode?: string;
  title?: string;
  onClose: () => void;
}

/**
 * eproUrl === 1 时，量表查看弹窗：
 * 左侧时间轴展示该量表在各访视下的填报记录（点击切换），
 * 右侧根据选中的 scalaServerRecordId 调 /data/report/crf/get 渲染 PMP 表单。
 */
const PmpScaleModal: React.FC<PmpScaleModalProps> = ({
  visible,
  scaleCode,
  visitId,
  subjectCode,
  title,
  onClose,
}) => {
  const [visitList, setVisitList] = useState<{ label: string; value: any }[]>(
    []
  );
  const [curVisitId, setCurVisitId] = useState<any>(visitId);
  const [recordList, setRecordList] = useState<RecordItem[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);

  const [loadingForm, setLoadingForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schema, setSchema] = useState<SchemaSnapshot | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [submitTime, setSubmitTime] = useState("");
  const [crfName, setCrfName] = useState("");

  // 打开弹窗时加载访视下拉与记录列表
  useEffect(() => {
    if (!visible) return;
    setSelectedId(null);
    setSchema(null);
    setValues({});
    setSubmitTime("");
    setCrfName("");
    setRecordList([]);

    if (scaleCode && subjectCode) {
      getVisitsByScaleCode({ scaleCode, subjectCode })
        .then(({ data }) => {
          const list = (data ?? []).map(({ id, name }: any) => ({
            label: name,
            value: id,
          }));
          setVisitList(list);
        })
        .catch(() => setVisitList([]));
    }
    setCurVisitId(visitId);
    loadRecords(visitId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, scaleCode, visitId, subjectCode]);

  // 加载某访视下的填报记录
  const loadRecords = async (vid: any) => {
    if (!vid || !scaleCode) return;
    try {
      setLoadingRecords(true);
      const { data } = await getReportedById({ scaleCode, visitId: vid });
      const list: RecordItem[] = (data ?? []).map(
        ({ createTime, ...reset }: any) => ({
          ...reset,
          createTime: createTime
            ? moment(createTime).format("YYYY-MM-DD HH:mm")
            : "",
        })
      );
      setRecordList(list);
      // 默认选中最新一条
      if (list.length) {
        setSelectedId(list[list.length - 1].scalaServerRecordId ?? null);
      } else {
        setSelectedId(null);
      }
    } catch {
      setRecordList([]);
      setSelectedId(null);
    } finally {
      setLoadingRecords(false);
    }
  };

  // 选中记录后加载 PMP 表单
  useEffect(() => {
    if (!selectedId) return;
    let mounted = true;
    setLoadingForm(true);
    setError(null);
    getPmpCrfRecord(selectedId)
      .then((res: any) => {
        if (!mounted) return;
        if (res.code !== 0) {
          setError(res.msg || "PMP 接口返回异常");
          return;
        }
        const d = res.data || {};
        setCrfName(d.crfName || "");
        if (d.schemaSnapshot) {
          try {
            setSchema(
              typeof d.schemaSnapshot === "string"
                ? JSON.parse(d.schemaSnapshot)
                : d.schemaSnapshot
            );
          } catch {
            setSchema(null);
          }
        }
        if (d.dataJson) {
          try {
            setValues(
              typeof d.dataJson === "string"
                ? JSON.parse(d.dataJson)
                : d.dataJson
            );
          } catch {
            setValues({});
          }
        }
        if (d.submitTime) {
          setSubmitTime(
            moment(Number(d.submitTime)).format("YYYY-MM-DD HH:mm:ss")
          );
        }
      })
      .catch((e: any) => {
        if (mounted) setError(e?.message || "加载失败");
      })
      .finally(() => {
        if (mounted) setLoadingForm(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedId]);

  // 把 node-tree 的 schema 拍平成字段列表
  const fieldList = useMemo(() => {
    if (!schema?.root) return [];
    const result: Array<{ section: string; field: FieldNode }> = [];
    const walk = (node: FieldNode, sectionTitle: string) => {
      if (node.nodeType === "field" || node.type) {
        result.push({ section: sectionTitle, field: node });
      }
      if (node.children) {
        node.children.forEach((child) =>
          walk(child, node.type ? sectionTitle : (node as any).title || sectionTitle)
        );
      }
    };
    walk(schema.root, "");
    return result;
  }, [schema]);

  const renderFieldValue = (field: FieldNode) => {
    const raw = values?.[field.fieldId as string];
    if (raw === undefined || raw === null || raw === "") {
      return <Text type="secondary">—</Text>;
    }
    if (field.type === "likert" && Array.isArray(field.options)) {
      return (
        <div className="pmp-option-list">
          {field.options.map((o) => {
            const checked = String(o.value) === String(raw);
            return (
              <div
                key={String(o.value)}
                className={`pmp-option ${checked ? "checked" : ""}`}
              >
                <span className="pmp-option__dot" />
                <span className="pmp-option__label">{o.label}</span>
                {checked && (
                  <span className="pmp-option__score">得分：{o.score ?? "—"}</span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    if (field.type === "file") {
      const urls: string[] = Array.isArray(raw) ? raw : [raw];
      const valid = urls.filter((u) => typeof u === "string" && !!u);
      if (!valid.length) return <Text type="secondary">—</Text>;
      return (
        <Image.PreviewGroup>
          <div className="pmp-file-list">
            {valid.map((url, i) => (
              <Image
                key={`${url}-${i}`}
                src={url}
                className="pmp-file-thumb"
                width={96}
                height={96}
                style={{ borderRadius: 8, objectFit: "cover" }}
              />
            ))}
          </div>
        </Image.PreviewGroup>
      );
    }
    return <Text>{String(raw)}</Text>;
  };

  const handleVisitChange = (value: any) => {
    setCurVisitId(value);
    loadRecords(value);
  };

  return (
    <Modal
      title={title || crfName || "量表记录"}
      open={visible}
      footer={null}
      width={1000}
      destroyOnClose
      maskClosable={false}
      onCancel={onClose}
      wrapClassName="pmp-scale--modal"
    >
      <div className="pmp-modal">
        <div className="pmp-modal__left">
          <div className="pmp-modal__left-header">
            <Select
              value={curVisitId}
              onChange={handleVisitChange}
              bordered={false}
              className="pmp-modal__visit-select"
              options={visitList}
              placeholder="选择访视"
            />
          </div>
          <div className="pmp-modal__timeline">
            {loadingRecords && <div className="pmp-modal__tip">加载中...</div>}
            {!loadingRecords && recordList.length === 0 && (
              <div className="pmp-modal__tip">暂无记录</div>
            )}
            {!loadingRecords && recordList.length > 0 && (
              <Timeline>
                {recordList.map((r) => (
                  <Timeline.Item
                    key={r.scalaServerRecordId}
                    color={
                      r.scalaServerRecordId === selectedId ? "#1890ff" : "gray"
                    }
                    onClick={() =>
                      setSelectedId(r.scalaServerRecordId ?? null)
                    }
                    className={`pmp-timeline-item ${
                      r.scalaServerRecordId === selectedId ? "active" : ""
                    }`}
                  >
                    <span className="pmp-timeline-item__time">
                      {r.createTime || "未记录时间"}
                    </span>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </div>
        </div>
        <div className="pmp-modal__right">
          <div className="pmp-modal__right-header">
            <span className="pmp-modal__subtitle">评估问卷</span>
            {submitTime && (
              <span className="pmp-modal__submit-time">
                提交时间：{submitTime}
              </span>
            )}
          </div>
          <div className="pmp-modal__form">
            {loadingForm && (
              <div className="pmp-modal__loading">
                <Spin tip="加载中..." />
              </div>
            )}
            {!loadingForm && error && (
              <Alert type="error" showIcon message="加载失败" description={error} />
            )}
            {!loadingForm && !error && (
              <>
                {schema?.description && (
                  <Paragraph className="pmp-modal__desc">
                    {schema.description}
                  </Paragraph>
                )}
                {fieldList.length === 0 && (
                  <Alert type="info" showIcon message="暂无量表字段" />
                )}
                {fieldList.map(({ section, field }, idx) => (
                  <div key={field.fieldId || idx} className="pmp-modal__field">
                    {section && (
                      <div className="pmp-modal__section">{section}</div>
                    )}
                    <div className="pmp-modal__field-row">
                      <div className="pmp-modal__field-title">
                        {field.required && (
                          <span className="pmp-modal__required">*</span>
                        )}
                        <Text strong>{field.label}</Text>
                      </div>
                      <div className="pmp-modal__field-value">
                        {renderFieldValue(field)}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PmpScaleModal;
